import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { startAuth, verifyAuth } from "@/lib/api";
import { inputCls, labelCls } from "@/lib/modal";
import { COUNTRIES, STATES } from "@/lib/geo";
import { Loader2, Copy, Check, Share2, ArrowUpRight, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const initial = { name: "", email: "", mobile: "", country_code: "+1", country: "", state: "", consent: false };

const COUNTRY_CODES = [
  { code: "+1", name: "United States / Canada" },
  { code: "+44", name: "United Kingdom" },
  { code: "+91", name: "India" },
  { code: "+61", name: "Australia" },
  { code: "+49", name: "Germany" },
  { code: "+33", name: "France" },
  { code: "+34", name: "Spain" },
  { code: "+39", name: "Italy" },
  { code: "+81", name: "Japan" },
  { code: "+82", name: "South Korea" },
  { code: "+86", name: "China" },
  { code: "+7", name: "Russia" },
  { code: "+55", name: "Brazil" },
  { code: "+52", name: "Mexico" },
  { code: "+62", name: "Indonesia" },
  { code: "+65", name: "Singapore" },
  { code: "+90", name: "Turkey" },
  { code: "+234", name: "Nigeria" },
  { code: "+27", name: "South Africa" },
  { code: "+971", name: "UAE" },
];

const errorText = (err, fallback) => {
  const msg = err.response?.data?.detail;
  return typeof msg === "string" ? msg : fallback;
};

export const JoinModal = ({ open, onOpenChange, referral }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form");
  const [mode, setMode] = useState("signup");
  const [otp, setOtp] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await startAuth({
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim() ? `${form.country_code} ${form.mobile.trim()}` : null,
        country: form.country.trim() || null,
        state: form.state.trim() || null,
        referral: referral || null,
      });
      setMode(res.mode || "signup");
      setStep("otp");
    } catch (err) {
      toast.error(errorText(err, "Could not start registration. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const data = await verifyAuth({ email: form.email.trim(), otp: otp.trim() });
      setResult(data.member);
      setStep("done");
    } catch (err) {
      toast.error(errorText(err, "Verification failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await startAuth({
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim() ? `${form.country_code} ${form.mobile.trim()}` : null,
        country: form.country.trim() || null,
        state: form.state.trim() || null,
        referral: referral || null,
      });
      toast.success("A new code is on its way.");
    } catch (err) {
      toast.error(errorText(err, "Could not resend the code."));
    }
  };

  const shareUrl = result ? `${window.location.origin}/invite/${result.genesis_id}` : "";
  const shareText = encodeURIComponent(
    `I just joined the Synaphy contributor waitlist · the Physical World Intelligence Network. Founding Phase is open: ${shareUrl}`
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setResult(null); setForm(initial); setStep("form"); setOtp(""); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-lenis-prevent
        className="bg-[#0A0A0A] border-white/10 text-white sm:rounded-2xl max-h-[90vh] overflow-y-auto overscroll-contain shadow-[0_0_80px_rgba(0,240,255,0.08)]"
        data-testid="join-modal"
      >
        {step === "form" && (
          <>
            <DialogHeader>
              <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-400 mb-2">
                Founding Phase · Contributor Waitlist
              </div>
              <DialogTitle className="font-display text-2xl font-medium tracking-tight text-white">
                Join the Contributor Waitlist
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm leading-relaxed">
                Reserve your contributor position in the Physical World Intelligence
                Network. Free to join. No payment required.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-2 space-y-5">
              {referral && (
                <div
                  className="rounded-lg border border-cyan-400/25 bg-cyan-400/[0.05] px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-cyan-300"
                  data-testid="join-referral-note"
                >
                  INVITE CODE APPLIED · {referral}
                </div>
              )}
              <div>
                <label htmlFor="join-name" className={labelCls}>Name</label>
                <input
                  id="join-name"
                  data-testid="join-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="join-email" className={labelCls}>Email</label>
                <input
                  id="join-email"
                  data-testid="join-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="join-mobile" className={labelCls}>Mobile Number (optional)</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    id="join-country-code"
                    data-testid="join-country-code-select"
                    value={form.country_code}
                    onChange={set("country_code")}
                    className={`${inputCls} sm:w-[180px] sm:shrink-0 appearance-none cursor-pointer`}
                    aria-label="Country code"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code} className="bg-[#0A0A0A] text-white">
                        {c.code} · {c.name}
                      </option>
                    ))}
                  </select>
                  <input
                    id="join-mobile"
                    data-testid="join-mobile-input"
                    value={form.mobile}
                    onChange={set("mobile")}
                    placeholder="555 000 1111"
                    className={`${inputCls} min-w-0 flex-1`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="join-country" className={labelCls}>Country</label>
                  <select
                    id="join-country"
                    data-testid="join-country-input"
                    required
                    value={form.country}
                    onChange={(e) => setForm((f) => ({ ...f, country: e.target.value, state: "" }))}
                    className={`${inputCls} bg-[#0A0A0A] appearance-none`}
                  >
                    <option value="" className="bg-[#0A0A0A] text-white">Select country</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c} className="bg-[#0A0A0A] text-white">{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="join-state" className={labelCls}>State</label>
                  {STATES[form.country] ? (
                    <select
                      id="join-state"
                      data-testid="join-state-input"
                      required
                      value={form.state}
                      onChange={set("state")}
                      className={`${inputCls} bg-[#0A0A0A] appearance-none`}
                    >
                      <option value="" className="bg-[#0A0A0A] text-white">Select state</option>
                      {STATES[form.country].map((s) => (
                        <option key={s} value={s} className="bg-[#0A0A0A] text-white">{s}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id="join-state"
                      data-testid="join-state-input"
                      value={form.state}
                      onChange={set("state")}
                      placeholder={form.country ? "State / region" : "Select country first"}
                      maxLength={80}
                      required
                      disabled={!form.country}
                      className={`${inputCls} disabled:opacity-50`}
                    />
                  )}
                </div>
              </div>
              <label
                className="flex items-start gap-3 cursor-pointer select-none"
                data-testid="join-consent-label"
              >
                <input
                  type="checkbox"
                  data-testid="join-consent-checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 shrink-0 appearance-none rounded border border-white/25 bg-[#0A0A0A] transition-colors checked:border-cyan-400 checked:bg-cyan-400 cursor-pointer"
                  style={form.consent ? { backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 12 10' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 5.5 4.5 9 11 1' stroke='%23000' stroke-width='2' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "center", backgroundSize: "10px" } : undefined}
                />
                <span className="text-[12px] leading-relaxed text-zinc-400">
                  I agree to the SYNAPHY{" "}
                  <Link to="/privacy" target="_blank" className="text-cyan-400 hover:underline" data-testid="join-consent-privacy-link">Privacy</Link>
                  {" "}&amp;{" "}
                  <Link to="/terms" target="_blank" className="text-cyan-400 hover:underline" data-testid="join-consent-terms-link">Terms</Link>
                </span>
              </label>
              <button
                type="submit"
                data-testid="join-submit-btn"
                disabled={loading || !form.consent}
                className="w-full rounded-full bg-cyan-400 py-3.5 font-display text-sm font-semibold tracking-wider text-black transition-[background-color,box-shadow] duration-300 hover:bg-cyan-300 hover:shadow-[0_0_28px_rgba(0,240,255,0.35)] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                CONTINUE
              </button>
              <p className="text-center text-[11px] text-zinc-600 leading-relaxed">
                We will email you a one-time verification code. Your Founding
                position is created only after your email is verified.
              </p>
            </form>
          </>
        )}

        {step === "otp" && (
          <form onSubmit={verify} className="py-2" data-testid="join-otp-step">
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-400 mb-2">
              Email verification
            </div>
            <DialogTitle className="font-display text-2xl font-medium tracking-tight text-white">
              Check your inbox
            </DialogTitle>
            <DialogDescription className="mt-3 text-zinc-500 text-sm leading-relaxed">
              We sent a 6-digit verification code to{" "}
              <span className="text-zinc-300">{form.email}</span>. It expires in
              10 minutes.
            </DialogDescription>
            {mode === "login" && (
              <div
                className="mt-5 flex items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-400/[0.07] px-4 py-3.5"
                data-testid="join-login-mode-note"
                role="alert"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-400" />
                <div>
                  <p className="text-[13px] font-semibold text-amber-200">
                    Account already exists
                  </p>
                  <p className="mt-1 text-[12px] leading-relaxed text-zinc-400">
                    This email is already registered - one email can hold only
                    one Founding account, so a new one cannot be created. We
                    sent a sign-in code instead; verify below to access your
                    existing account. Your display name stays as originally
                    registered.
                  </p>
                </div>
              </div>
            )}
            <div className="mt-6">
              <label htmlFor="join-otp" className={labelCls}>Verification code</label>
              <input
                id="join-otp"
                data-testid="join-otp-input"
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="······"
                className={`${inputCls} text-center font-mono text-2xl tracking-[0.5em]`}
              />
            </div>
            <button
              type="submit"
              data-testid="join-verify-btn"
              disabled={loading || otp.length !== 6}
              className="mt-5 w-full rounded-full bg-cyan-400 py-3.5 font-display text-sm font-semibold tracking-wider text-black transition-[background-color,box-shadow] duration-300 hover:bg-cyan-300 hover:shadow-[0_0_28px_rgba(0,240,255,0.35)] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              VERIFY &amp; JOIN
            </button>
            <div className="mt-4 flex items-center justify-between text-[11px] text-zinc-500">
              <button type="button" onClick={() => setStep("form")} className="hover:text-cyan-400 transition-colors" data-testid="join-back-btn">
                Change details
              </button>
              <button type="button" onClick={resend} className="hover:text-cyan-400 transition-colors" data-testid="join-resend-btn">
                Resend code
              </button>
            </div>
          </form>
        )}

        {step === "done" && result && (
          <div className="py-2" data-testid="join-success">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-400 mb-3">
              <ShieldCheck className="h-3.5 w-3.5" /> Email verified
            </div>
            <h3 className="font-display text-3xl font-medium tracking-tight text-white">
              Welcome to Synaphy
            </h3>
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
              Your email is verified and your Founding registration is complete. A
              confirmation email is on its way.
            </p>
            <div className="mt-6 rounded-xl border border-cyan-400/25 bg-cyan-400/[0.05] p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                Your Founding ID
              </div>
              <div className="font-mono text-xl sm:text-2xl tracking-[0.12em] text-cyan-400" data-testid="genesis-id-value">
                {result.genesis_id}
              </div>
              <div className="mt-2 font-mono text-[11px] tracking-[0.2em] text-zinc-500" data-testid="genesis-position-value">
                FOUNDING POSITION #{String(result.position).padStart(5, "0")}
              </div>
            </div>
            <a
              href="/portal"
              data-testid="join-portal-btn"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/[0.08] py-3 font-display text-sm font-semibold tracking-wider text-cyan-300 transition-colors hover:bg-cyan-400/[0.15]"
            >
              OPEN FOUNDING PORTAL
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <div className="mt-6">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
                <Share2 className="h-3.5 w-3.5 text-cyan-400" /> Share Synaphy
              </div>
              <div className="grid grid-cols-3 gap-2">
                <a
                  data-testid="share-whatsapp-btn"
                  href={`https://wa.me/?text=${shareText}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-white/10 bg-white/[0.03] py-2.5 text-center font-mono text-[11px] tracking-widest text-zinc-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-400"
                >
                  WHATSAPP
                </a>
                <a
                  data-testid="share-telegram-btn"
                  href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent("I just joined the Synaphy contributor waitlist · the Physical World Intelligence Network. Founding Phase is open.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-white/10 bg-white/[0.03] py-2.5 text-center font-mono text-[11px] tracking-widest text-zinc-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-400"
                >
                  TELEGRAM
                </a>
                <a
                  data-testid="share-x-btn"
                  href={`https://twitter.com/intent/tweet?text=${shareText}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-white/10 bg-white/[0.03] py-2.5 text-center font-mono text-[11px] tracking-widest text-zinc-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-400"
                >
                  X
                </a>
              </div>
              <button
                data-testid="copy-referral-btn"
                onClick={copy}
                className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.03] py-2.5 font-mono text-[11px] tracking-widest text-zinc-400 transition-colors hover:border-cyan-400/50 hover:text-cyan-400 flex items-center justify-center gap-2"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "COPIED" : "COPY REFERRAL LINK"}
              </button>
              <p className="mt-4 text-center text-[11px] leading-relaxed text-zinc-500">
                Every verified invite moves you up the Founding leaderboard.
                5 / 10 / 25 invites unlock SPARK, CONNECTOR and NETWORK BUILDER
                status, with an email the moment you level up.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
